import type { ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CloudUpload, HelpCircle } from "lucide-react";
import { useWizard, type DocumentMetadata } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { InfoCallout } from "../common/InfoCallout";
import { SectionCard } from "../common/SectionCard";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
import { ProgressCard } from "../common/ProgressCard";
import { cn } from "@/lib/utils";
import { CurrencySelect } from "@/components/workflow/common/CurrencySelect";

interface EthicsCardProps {
  onCollapse: () => void;
}

function FieldBlock({
  label,
  description,
  children,
}: {
  label: ReactNode;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label className="text-sm font-medium leading-5 text-slate-900">{label}</Label>
        <p className="text-sm leading-5 text-slate-500">{description}</p>
      </div>
      {children}
    </div>
  );
}

function ChoiceCard({
  checked,
  disabled,
  label,
  name,
  id,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  name: string;
  id: string;
  onChange: () => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-[72px] flex-1 cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors",
        checked ? "border-primary/20 bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white",
          checked ? "border-primary" : "border-slate-500",
        )}
      >
        <span className={cn("h-2 w-2 rounded-full bg-primary", !checked && "opacity-0")} />
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}

function UploadIcon() {
  return (
    <div className="rounded-full bg-slate-500/5 p-4">
      <CloudUpload className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.5} />
    </div>
  );
}

export function EthicsCard({ onCollapse }: EthicsCardProps) {
  const {
    ethicsData,
    updateEthicsData,
    ethicsCompletion,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
  } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();
  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Ethics data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Ethics (B11)"}
        description={"Tell us whether your business faced legal penalties for corruption or bribery, and whether you have measures to prevent it"}
        onClose={onCollapse}
        onSave={() => {
          void handleSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Governance"}
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"Convictions & Fines"}
                </h3>
                <HelpCircle className="h-[15px] w-[15px] shrink-0 text-slate-500" />
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-6">
            <FieldBlock
              label={
                <>
                  {"Convictions & Fines"} <span className="text-red-500">*</span>
                </>
              }
              description={"In the past year, was your company convicted of, or fined for, any corruption or bribery offence?"}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ChoiceCard
                  checked={ethicsData.hasConvictions === true}
                  disabled={disabled}
                  label={"Yes"}
                  name="convictions"
                  id="conv_yes"
                  onChange={() => updateEthicsData({ hasConvictions: true })}
                />
                <ChoiceCard
                  checked={ethicsData.hasConvictions === false}
                  disabled={disabled}
                  label={"No"}
                  name="convictions"
                  id="conv_no"
                  onChange={() => updateEthicsData({ hasConvictions: false })}
                />
              </div>
            </FieldBlock>

            {ethicsData.hasConvictions === true ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-3">
                  <Label className="text-sm font-medium leading-5 text-slate-900">
                    {"If Yes: Number of convictions"} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder={"0"}
                    value={ethicsData.convictionsCount || ""}
                    onChange={(e) => updateEthicsData({ convictionsCount: e.target.value })}
                    disabled={disabled}
                    className="form-input h-12 rounded-2xl px-4 text-base"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <Label className="text-sm font-medium leading-5 text-slate-900">
                    {"Total amount of fines (if any)"} <span className="text-red-500">*</span>
                  </Label>
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={"e.g. 50000"}
                      value={ethicsData.finesAmount || ""}
                      onChange={(e) => updateEthicsData({ finesAmount: e.target.value })}
                      disabled={disabled}
                      className="form-input h-12 rounded-2xl px-4 text-base"
                    />
                    <CurrencySelect
                      value={ethicsData.finesCurrency || "usd"}
                      onValueChange={(value) => updateEthicsData({ finesCurrency: value })}
                      disabled={disabled}
                      lowercase
                      triggerClassName="form-input h-12 rounded-2xl px-4 text-base"
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </SectionCard>

        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"Anti-Corruption Policy (Optional)"}
                </h3>
                <HelpCircle className="h-[15px] w-[15px] shrink-0 text-slate-500" />
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-6">
            <FieldBlock
              label={"Anti-Corruption Policy (Optional)"}
              description={"Do you have a code of conduct or anti-corruption policy in place?"}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ChoiceCard
                  checked={ethicsData.antiCorruptionPolicy === true}
                  disabled={disabled}
                  label={"Yes"}
                  name="policy"
                  id="policy_yes"
                  onChange={() => updateEthicsData({ antiCorruptionPolicy: true })}
                />
                <ChoiceCard
                  checked={ethicsData.antiCorruptionPolicy === false}
                  disabled={disabled}
                  label={"No"}
                  name="policy"
                  id="policy_no"
                  onChange={() => updateEthicsData({ antiCorruptionPolicy: false })}
                />
              </div>
            </FieldBlock>

            <div className="rounded-[24px] border-2 border-slate-200 bg-slate-50 p-4 sm:p-10">
              <DocumentUploadSection
                fieldIdentifier="governance-ethics"
                documents={(ethicsData.evidenceFiles || []) as DocumentMetadata[]}
                onDocumentsChange={(docs: DocumentMetadata[]) =>
                  updateEthicsData({ evidenceFiles: docs as any })
                }
                currentTab="governance"
                label={"Policy Documents (Optional)"}
                description={"Upload your code of conduct or anti-corruption policy document"}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.csv"
                maxFiles={5}
                borderColor="border-slate-200"
                hideHeader
                className="bg-transparent p-0"
                emptyStateButtonClassName="rounded-[24px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                documentsListClassName="space-y-3"
                itemClassName="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                actionButtonClassName="rounded-3xl border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                emptyStateContent={
                  <div className="flex flex-col items-center text-center">
                    <div className="pb-4">
                      <UploadIcon />
                    </div>
                    <p className="text-base font-bold leading-6 text-slate-800">
                      Click to upload or drag and drop
                    </p>
                    <p className="pt-1 text-sm leading-5 text-slate-500">
                      {"Upload your code of conduct or anti-corruption policy document"}
                    </p>
                  </div>
                }
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"Whistleblowing / Reporting Channel (Optional)"}
                </h3>
                <HelpCircle className="h-[15px] w-[15px] shrink-0 text-slate-500" />
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-6">
            <FieldBlock
              label={"Whistleblowing / Reporting Channel (Optional)"}
              description={"Do you have a whistleblowing or reporting channel for employees?"}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ChoiceCard
                  checked={ethicsData.whistleblowerMechanism === true}
                  disabled={disabled}
                  label={"Yes"}
                  name="whistleblowing"
                  id="whistleblowing_yes"
                  onChange={() => updateEthicsData({ whistleblowerMechanism: true })}
                />
                <ChoiceCard
                  checked={ethicsData.whistleblowerMechanism === false}
                  disabled={disabled}
                  label={"No"}
                  name="whistleblowing"
                  id="whistleblowing_no"
                  onChange={() => updateEthicsData({ whistleblowerMechanism: false })}
                />
              </div>
            </FieldBlock>

            <InfoCallout
              label={"Note:"}
              text={"A whistleblowing channel gives employees a safe, confidential way to raise concerns about dishonest or unethical behavior — without fear of consequences"}
            />
          </div>
        </SectionCard>

        <TransitionMeasuresBlock topicId="ethics" currentTab="governance" disabled={disabled} />

        <ProgressCard completion={ethicsCompletion} />
      </div>
    </div>
  );
}
